import { useState, type ReactNode } from 'react';
import { useIsCompact } from '../lib/useMediaQuery';
import brandMark from '../assets/brand/appealstack-icon-white.svg';
import { LegalFooterLinks } from '../pages/LegalPage';

/**
 * PortalLayout — the shared shell for the external portals (WS-3.2).
 *
 * Deliberately self-contained and simpler than the operator AppLayout: external
 * users (client / consultant) get a focused, tabbed surface, not the full
 * operator sidebar + generic-CRUD navigation. Tab switching is local state (no
 * URL routing) so the portal apps stay within the S07 base:'/' routing without
 * claiming their own route tree. Themed via the same CSS-variable tokens so org
 * branding still applies.
 *
 * On desktop the tabs are a horizontal bar; below the compact breakpoint they
 * collapse into a single hamburger menu so the nav never crowds a phone header.
 */

export interface PortalTab {
  key: string;
  label: string;
  /** Optional unread count rendered as a pill on the tab (additive; omit → none). */
  badge?: number;
}

/** Small unread-count pill shown on a portal tab. */
function TabBadge({ count }: { count: number }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 18, height: 18, padding: '0 5px', marginLeft: 6,
        borderRadius: 999, fontSize: 11, fontWeight: 700, lineHeight: 1,
        background: 'var(--color-status-red, #c0392b)', color: '#fff',
      }}
    >{count}</span>
  );
}

export interface PortalLayoutProps {
  brandName: string;
  portalLabel: string;
  userLabel?: string;
  tabs: PortalTab[];
  active: string;
  onSelect: (key: string) => void;
  onLogout: () => void;
  children: ReactNode;
}

export function PortalLayout({ portalLabel, userLabel, tabs, active, onSelect, onLogout, children }: PortalLayoutProps) {
  const compact = useIsCompact();
  const [menuOpen, setMenuOpen] = useState(false);
  const activeTab = tabs.find((t) => t.key === active);

  const pick = (key: string) => {
    onSelect(key);
    setMenuOpen(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-surface, #f7f7f5)', color: 'var(--color-ink, #1a1a1a)' }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: 56, background: 'var(--brand-primary, #14532d)',
        color: 'var(--brand-on-primary, #fff)', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <img src={brandMark} alt="" aria-hidden="true" width={26} height={26} style={{ display: 'block', flexShrink: 0 }} />
          <strong style={{ fontSize: 17, letterSpacing: '-0.01em' }}>appealstack</strong>
          <span style={{ opacity: 0.8, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{portalLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {userLabel && !compact && <span style={{ fontSize: 13, opacity: 0.9 }}>{userLabel}</span>}
          {/* Explicit light-on-dark styling: a ghost Button renders dark ink and is
              invisible on the dark-green portal header. A bordered, on-primary button
              makes "Sign out" clearly visible + discoverable for a client. */}
          <button
            type="button"
            onClick={onLogout}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '6px 12px',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: 'var(--r-md, 8px)',
              background: 'rgba(255,255,255,0.08)',
              color: 'var(--brand-on-primary, #fff)',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {compact ? (
        <nav style={{ background: 'var(--color-surface-raised, #fff)', borderBottom: '1px solid var(--color-border, #e2e2df)', position: 'relative', zIndex: 30 }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-label="Portal menu"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              appearance: 'none', border: 'none', background: 'none', cursor: 'pointer',
              padding: '14px 16px', fontSize: 15, fontWeight: 600,
              color: 'var(--brand-primary, #14532d)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            <span>{activeTab?.label ?? 'Menu'}</span>
          </button>
          {menuOpen && (
            <ul style={{
              listStyle: 'none', margin: 0, padding: '4px 0',
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
              background: 'var(--color-surface-raised, #fff)',
              borderBottom: '1px solid var(--color-border, #e2e2df)',
              boxShadow: 'var(--sh-lg, 0 8px 24px rgba(0,0,0,0.12))',
            }}>
              {tabs.map((t) => {
                const isActive = t.key === active;
                return (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => pick(t.key)}
                      style={{
                        appearance: 'none', border: 'none', background: isActive ? 'var(--brand-primary-bg, #eef4f0)' : 'none',
                        cursor: 'pointer', width: '100%', textAlign: 'left',
                        padding: '12px 16px', fontSize: 15,
                        color: isActive ? 'var(--brand-primary, #14532d)' : 'var(--color-ink, #1a1a1a)',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {t.label}
                      {t.badge ? <TabBadge count={t.badge} /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      ) : (
        <nav style={{
          display: 'flex', gap: 4, padding: '0 16px', borderBottom: '1px solid var(--color-border, #e2e2df)',
          background: 'var(--color-surface-raised, #fff)', overflowX: 'auto',
        }}>
          {tabs.map((t) => {
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                onClick={() => onSelect(t.key)}
                style={{
                  appearance: 'none', border: 'none', background: 'none', cursor: 'pointer',
                  padding: '14px 14px', fontSize: 14, whiteSpace: 'nowrap',
                  color: isActive ? 'var(--brand-primary, #14532d)' : 'var(--color-muted, #666)',
                  fontWeight: isActive ? 600 : 400,
                  borderBottom: isActive ? '2px solid var(--brand-primary, #14532d)' : '2px solid transparent',
                }}
              >
                {t.label}
                {t.badge ? <TabBadge count={t.badge} /> : null}
              </button>
            );
          })}
        </nav>
      )}

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        {children}
      </main>

      {/* The portal is where an EXTERNAL end user — a firm's own client, who
          never agreed to anything with us — actually needs the privacy notice,
          so it is linked from every portal page rather than only from sign-in.
          Plain anchors (see LegalFooterLinks) so this shell stays renderable
          without a Router in its unit tests. */}
      <footer style={{
        maxWidth: 1100, margin: '0 auto', padding: '8px 16px 24px',
        borderTop: '1px solid var(--color-border, #e2e2df)',
      }}>
        <LegalFooterLinks style={{ paddingTop: 12 }} />
      </footer>
    </div>
  );
}
